// Default-value editor for a visual criterion (prompt variable).
//
// Besides a plain literal, the author can bind the default to a TOKEN that
// resolves server-side at render time — so the visual filters correctly even
// when a smart page supplies no value. The token vocabulary mirrors what a
// transaction column default supports:
//   • Calendar values  — :calendar_today, :calendar_month_start, …
//   • Current user      — :user_login / :user_email / :user_employee / :user_site
//   • Project variables — :<name> (fixed- or SQL-backed)
// Resolution lives in the engine's `visual_param_tokens` module (both the
// designer backend and the standalone runtime server use it).

import { useMemo } from 'react'
import { ChipInput } from '@twasta/page-runtime/txn'
import { CALENDAR_TOKENS, CALENDAR_TOKEN_LABELS, projectVarToken } from '@/livepreview/utils'
import type { ProjectVariable } from '@/api/projectVariables'

// Mirror of `visual_param_tokens.USER_TOKEN_KEYS` (server-resolved).
export const USER_INFO_TOKENS: { token: string; label: string }[] = [
  { token: ':user_login', label: 'Logged-in user (login id)' },
  { token: ':user_email', label: 'Logged-in user email' },
  { token: ':user_employee', label: 'Employee code' },
  { token: ':user_site', label: 'Site / location code' },
]

const LITERAL = '__literal__'

interface Props {
  value: string
  projectVars: ProjectVariable[]
  onChange: (value: string) => void
  inputStyle?: React.CSSProperties
  selectStyle?: React.CSSProperties
  // IN-operator criteria: the literal default is a LIST — render a chip input
  // (stored comma-joined, the format the engine splits into the IN binds).
  chips?: boolean
  /** HTML input type for chip entry ('text' | 'number' | 'date'). */
  chipInputType?: string
}

export default function CriteriaDefaultField({
  value, projectVars, onChange, inputStyle, selectStyle, chips, chipInputType,
}: Props) {
  const calTokens = CALENDAR_TOKENS as readonly string[]
  const userTokens = useMemo(() => new Set(USER_INFO_TOKENS.map((u) => u.token)), [])
  const varByToken = useMemo(() => {
    const m = new Map<string, ProjectVariable>()
    for (const v of projectVars) m.set(projectVarToken(v.name), v)
    return m
  }, [projectVars])

  const v = value || ''
  const isCal = calTokens.includes(v)
  const isUser = userTokens.has(v)
  const isVar = varByToken.has(v)
  // Any other ':'-prefixed value is an unknown / hand-typed token — keep it
  // selectable as a project-variable-style token rather than losing it.
  const isUnknownToken = !isCal && !isUser && !isVar && v.startsWith(':')
  const isToken = isCal || isUser || isVar || isUnknownToken
  const selectValue = isToken ? v : LITERAL

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <select
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value
          // Switching to "Literal" clears any token so the input drives the
          // value; picking a token stores it verbatim.
          onChange(next === LITERAL ? '' : next)
        }}
        style={selectStyle}
      >
        <option value={LITERAL}>Literal value…</option>
        <optgroup label="Calendar">
          {calTokens.map((t) => (
            <option key={t} value={t}>
              {CALENDAR_TOKEN_LABELS[t as keyof typeof CALENDAR_TOKEN_LABELS] || t}
            </option>
          ))}
        </optgroup>
        <optgroup label="Current user">
          {USER_INFO_TOKENS.map((u) => (
            <option key={u.token} value={u.token}>{u.label}</option>
          ))}
        </optgroup>
        {projectVars.length > 0 && (
          <optgroup label="Project variables">
            {projectVars.map((pv) => (
              <option key={pv.name} value={projectVarToken(pv.name)}>
                {pv.description || pv.name}{pv.kind === 'sql' ? ' (SQL)' : ''}
              </option>
            ))}
          </optgroup>
        )}
        {isUnknownToken && <option value={v}>{v}</option>}
      </select>

      {isToken ? (
        <div style={{
          fontFamily: 'monospace', fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)',
          padding: '4px 8px',
          border: '1px dashed var(--color-border)', borderRadius: 4,
        }}>
          {v} <span style={{ fontStyle: 'italic' }}>· resolved at render time</span>
        </div>
      ) : chips ? (
        <ChipInput
          values={v.split(',').map((s) => s.trim()).filter(Boolean)}
          onChange={(list) => onChange(list.join(','))}
          inputType={chipInputType || 'text'}
          placeholder="Add value…"
        />
      ) : (
        <input
          type="text"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
          placeholder="(empty)"
        />
      )}
    </div>
  )
}
